/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Component imports
import { Button, Col, Grid, Input, Popconfirm, Row, Table, Typography, Tooltip, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import Permissioned from '../Permissioned';
import ReadMorePopover from '../UI/ReadMorePopover';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { LANGUAGE_OPTIONS, PERMISSIONS_GROUP_EDITOR, DEFAULT_DATE_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import { truncate } from '../../utils/string/truncate';
import { TEST_SET_TABS } from '../../constants/testSets';
import { useTestSetNavigation } from '../../utils/hooks/useTestSetsNavigation';
import { useDeleteTestSetMutation, useFetchTestSetsQuery } from '../../store/testSets/testSetsApi';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import useUserPermissions from '../../utils/hooks/useUserPermissions';

// Constant declarations
const { Text } = Typography;
const { useBreakpoint } = Grid;

const TestSetsTable = () => {
  const { tenantId, orgId } = useParams();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams({});
  const screens = useBreakpoint();
  const { navigateToTestSet, navigateToCreateTestSet } = useTestSetNavigation();
  const { handleMouseDown, isDragging } = useDragDetection();
  const isMobileSize = screens.xs;

  const [deleteTestSet, { isLoading: isDeletingTestSet }] = useDeleteTestSetMutation({ fixedCacheKey: 'delete-test-set' });
  const [tableData, setTableData] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [columns, setColumns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const { data: testSets, isFetching: isFetchingTestSets } = useFetchTestSetsQuery({ orgId, tenantId, urlParams: {
    page: searchParams.get('page'),
    order: searchParams.get('order'),
    sortBy: searchParams.get('sortBy'),
    pageSize: pageSize
  } }, { skip: !(searchParams.get('page') || searchParams.get('order') || searchParams.get('sortBy')) });

  useEffect(() => {
    const filteredData = testSets?.items.filter(i => i.name.toLowerCase().includes(searchFilter.toLowerCase()) || i.description?.toLowerCase().includes(searchFilter.toLowerCase()));
    setTableData(filteredData);
  }, [searchFilter, testSets?.items]);

  useEffect(() => {
    setColumns([
      {
        title: t('Name'),
        key: 'name',
        dataIndex: 'name',
        render: (_, record) => {
          return (<Highlighter
            highlightClassName="bg-highlight p-0"
            searchWords={[searchFilter]}
            autoEscape
            textToHighlight={record.name}
          />);
        }
      },
      {
        title: t('Description'),
        dataIndex: 'description',
        responsive: ['sm'],
        key: 'description',
        render: (_, record) => {
          const { description } = record;
          if (!description) {
            return null;
          }

          return (
            <Fragment>
              <Highlighter
                highlightClassName="bg-highlight p-0"
                searchWords={[searchFilter]}
                autoEscape
                textToHighlight={truncate(description, 50)}
              />
              {
                description.length > 50 ? (
                  <ReadMorePopover
                    message={description}
                    searchFilter={searchFilter}
                    buttonClassName="ml-2"
                  />
                ) : null
              }
            </Fragment>
          );
        }
      },
      {
        title: t('Created by'),
        dataIndex: ['createdBy', 'name'],
        responsive: ['md'],
        key: 'createdBy'
      },
      {
        title: t('Last Update'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        responsive: ['lg'],
        sorter: (a, b) => a.updatedAt - b.updatedAt,
        render: (_text, record) => {
          if (record.updatedAt) {
            return moment(record.updatedAt).format(DEFAULT_DATE_FORMAT);
          } else {
            return '-';
          }
        }
      },
      {
        title: t('Language'),
        dataIndex: 'language',
        key: 'language',
        responsive: ['sm'],
        render: (_text, record) => <Text>{LANGUAGE_OPTIONS.find(lang => lang.key === record.language)?.label || record.language}</Text>
      },
      {
        title: t('Actions'),
        key: 'actions',
        fixed: 'right',
        width: 160,
        align: 'center',
        render: record => (
          <Fragment>
            <Permissioned
              allowedPermissions={PERMISSIONS_GROUP_EDITOR}
              currentPermissions={userPermissions}
              fallbackComponent={null}
            >
              <Space size="small" onClick={(e) => e.stopPropagation()}>
                <Tooltip overlayClassName="pointer-events-none" title={t('Edit Test')}>
                  <Button
                    type="text"
                    shape="circle"
                    icon={TEST_SET_TABS.TEST_SET.renderIcon()}
                    onClick={() => onTestSetSelect(record.id, TEST_SET_TABS.TEST_SET.key, record.name) }
                  />
                </Tooltip>

                <Tooltip overlayClassName="pointer-events-none" title={t('Run Test')}>
                  <Button
                    type="text"
                    shape="circle"
                    icon={TEST_SET_TABS.TEST_RUN.renderIcon()}
                    onClick={() => onTestSetSelect(record.id, TEST_SET_TABS.TEST_RUN.key, record.name)}
                  />
                </Tooltip>
                <Tooltip overlayClassName="pointer-events-none" title={t('View Test Set Results')}>
                  <Button
                    type="text"
                    shape="circle"
                    icon={TEST_SET_TABS.TEST_RESULTS.renderIcon()}
                    onClick={() => onTestSetSelect(record.id, TEST_SET_TABS.TEST_RESULTS.key, record.name) }
                  />
                </Tooltip>

                <Popconfirm
                  title={t('Are you sure you want to delete this test set?')}
                  okText={t('Yes')}
                  cancelText={t('No')}
                  placement="topRight"
                  onConfirm={() => onTestSetDelete(record.id)}
                >
                  <Tooltip overlayClassName="pointer-events-none" title={t('Delete Test')}>
                    <Button
                      type="text"
                      shape="circle"
                      icon={<DeleteIcon />}
                      loading={isDeletingTestSet}
                    />
                  </Tooltip>
                </Popconfirm>
              </Space>
            </Permissioned>
          </Fragment>
        )
      }
    ]);
  }, [tenantId, isMobileSize, searchFilter]);

  useEffect(() => {
    updateUrlSearchParams(Number(searchParams.get('page')) + 1);
  }, []);

  const onTestSetSelect = (id, tab, name) => {
    navigateToTestSet(id, tab, name);
  };

  const onTestSetDelete = async (id) => {
    deleteTestSet({ orgId, tenantId, nluTestId: id });
  };

  const handleTableChange = async (pagination, _filters, sorter) => {
    let sorting;
    if (sorter.column) {
      sorting = { [sorter.columnKey]: sorter.order === 'ascend' ? 'ASC' : 'DESC' };
    }
    updateUrlSearchParams(pagination.current, pagination.pageSize, sorting);
  };

  const updateUrlSearchParams = async (current = 1, pageSize = DEFAULT_PAGINATION_SIZE, sorting = { sendAt: 'DESC', status: 'DESC' }) => {
    setCurrentPage(current);
    setPageSize(pageSize);
    const urlSearchParams = new URLSearchParams(location.search);
    urlSearchParams.set('page', current - 1);

    if (sorting) {
      urlSearchParams.set('sortBy', Object.entries(sorting).flat()[0]);
      urlSearchParams.set('order', Object.entries(sorting).flat()[1]);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  return (
    <Fragment>
      <Permissioned
        allowedPermissions={PERMISSIONS_GROUP_EDITOR}
        currentPermissions={userPermissions}
        fallbackComponent={null}
      >
        <Row gutter={24} className="mb-2" wrap={screens.xs && !screens.sm}>
          <Col
            flex="auto"
            className="mb-2"
          >
            <Input
              size="large"
              style={{ maxWidth: screens.xs ? '100%' : 280 }}
              placeholder={t('Search in contents')}
              allowClear
              prefix={<SearchOutlined className="text-muted" />}
              className="w-100"
              onChange={(e) => {
                setSearchFilter(e.target.value);
              }}
            />
          </Col>

          <Col
            flex={screens.xs ? 'auto' : 'none'}
            className="mb-2"
          >
            <Button
              size="large"
              type="primary"
              icon={<PlusIcon />}
              className="float-right"
              onClick={navigateToCreateTestSet}
            >
              {t('Add Test Set')}
            </Button>
          </Col>
        </Row>
      </Permissioned>

      <Row gutter={24}>
        <Col xs={24}>
          <Table
            size="small"
            onChange={handleTableChange}
            onRow={(record) => ({
              onMouseDown: handleMouseDown,
              onClick: () => {
                if(!isDragging() && userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))) {
                  onTestSetSelect(record.id, TEST_SET_TABS.TEST_SET.key, record.name);
                }
              }
            })}
            rowClassName="cursor-pointer"
            rowKey="id"
            columns={columns}
            dataSource={tableData}
            loading={isFetchingTestSets}
            pagination={{
              size: 'default',
              total: testSets?.total,
              defaultCurrent: 1,
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total) => `${t('Total Tests')}: ${total}`
            }}
          />
        </Col>
      </Row>
    </Fragment>
  );
};

export default TestSetsTable;
